import { ComponentProps } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { MessageCircle, X, Loader2, RefreshCw, AlertCircle, ChevronLeft, Send, SendHorizontal } from 'lucide-react'
import { useChat } from '../hooks/use-chat'
import { cn } from '@/lib/utils'
import { useForm } from 'react-hook-form'
import { ErrorBoundary } from './ErrorBoundary'
import { Alert, AlertDescription } from '@/components/ui/alert'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface MessageInputForm {
  message: string
}

interface CodeBlockProps extends ComponentProps<'code'> {
  inline?: boolean
}

export default function ChatSupport() {
  const {
    messages,
    isOpen,
    isTyping,
    sendMessage,
    setIsOpen,
    error,
    clearMessages
  } = useChat()

  const scrollRef = useRef<HTMLDivElement>(null)
  const chatRef = useRef<HTMLDivElement>(null)
  const form = useForm<MessageInputForm>()
  const [autoScroll, setAutoScroll] = useState(true)
  const [showWelcome, setShowWelcome] = useState(true)
  const [retryMessage, setRetryMessage] = useState<string | null>(null)

  // Enhanced auto-scroll effect with user override
  useEffect(() => {
    if (scrollRef.current && autoScroll) {
      const scrollElement = scrollRef.current
      const isAtBottom =
        scrollElement.scrollHeight - scrollElement.scrollTop <=
        scrollElement.clientHeight + 100

      if (isAtBottom || messages.length <= 1) {
        scrollElement.scrollTo({
          top: scrollElement.scrollHeight,
          behavior: messages.length <= 1 ? 'auto' : 'smooth'
        })
      }
    }
  }, [messages, isTyping, autoScroll])

  // Scroll event handler to detect manual scrolling
  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    const handleScroll = () => {
      const isAtBottom =
        scrollElement.scrollHeight - scrollElement.scrollTop <=
        scrollElement.clientHeight + 100
      setAutoScroll(isAtBottom)
    }

    scrollElement.addEventListener('scroll', handleScroll)
    return () => scrollElement.removeEventListener('scroll', handleScroll)
  }, [])

  // Click outside handler with cleanup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (chatRef.current && !chatRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, setIsOpen])

  // Hide welcome message when first message arrives
  useEffect(() => {
    if (messages.length > 0) {
      setShowWelcome(false)
    }
  }, [messages])

  const handleSubmit = async (data: MessageInputForm) => {
    if (!data.message.trim() || isTyping) return
    setRetryMessage(data.message)
    form.reset()
    await sendMessage(data.message)
  }

  // Cleanup effect
  useEffect(() => {
    const cleanup = () => {
      setAutoScroll(true)
      setShowWelcome(true)
      setRetryMessage(null)
    }

    return cleanup
  }, [])

  // Helper to format message content with proper spacing
  const formatMessageContent = (content: string) => {
    return content.trim().replace(/\n{3,}/g, '\n\n')
  }

  // Enhanced code block rendering
  const renderMarkdown = (content: string) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ ...props }) => <p className="m-0 text-inherit font-figtree text-sm leading-4 font-normal" {...props} />,
        a: ({ ...props }) => (
          <a
            className="text-[#18181B] underline hover:text-[#18181B]/80 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
            {...props}
          />
        ),
        ul: ({ ...props }) => <ul className="my-1 list-disc pl-4" {...props} />,
        ol: ({ ...props }) => (
          <ol className="my-1 list-decimal pl-4" {...props} />
        ),
        li: ({ ...props }) => <li className="my-0.5" {...props} />,
        code: ({ className, children, inline, ...props }: CodeBlockProps) => {
          if (inline) {
            return (
              <code
                className="bg-primary/10 rounded px-1 py-0.5 font-mono text-sm"
                {...props}>
                {children}
              </code>
            )
          }
          return (
            <div className="relative group my-2">
              <div className="absolute -top-3 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => {
                    if (typeof children === 'string') {
                      navigator.clipboard.writeText(children)
                    }
                  }}>
                  Copy
                </Button>
              </div>
              <code
                className={cn(
                  'block bg-muted p-2 rounded-md font-mono text-sm overflow-x-auto',
                  className
                )}
                {...props}>
                {children}
              </code>
            </div>
          )
        },
        pre: ({ ...props }) => (
          <pre className="bg-transparent p-0 my-0" {...props} />
        )
      }}>
      {formatMessageContent(content)}
    </ReactMarkdown>
  )

  return (
    <ErrorBoundary>
      <Button
        size="icon"
        className={cn(
          'fixed bottom-4 right-4 h-14 w-14 rounded-full shadow-2xl z-[9999] transition-all duration-300',
          'bg-primary hover:bg-primary/90 ring-2 ring-background',
          'animate-in fade-in-50',
          isOpen && 'translate-y-2 opacity-0 pointer-events-none'
        )}
        onClick={() => setIsOpen(true)}>
        <MessageCircle className="h-8 w-8 text-primary-foreground" />
      </Button>

      {isOpen && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[9998] md:hidden animate-in fade-in-0 duration-200" />
      )}

      {isOpen && (
        <div
          ref={chatRef}
          className={cn(
            'fixed z-[9999] animate-in duration-300',
            'inset-0 md:inset-auto md:bottom-20 md:right-4 md:w-[400px]',
            'slide-in-from-bottom md:slide-in-from-right'
          )}>
          <Card className="h-full md:h-[600px] shadow-2xl flex flex-col bg-[#18181B]">
            <div className="flex items-center justify-between -b p-4 bg-[#18181B] text-primary-foreground">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center bg-[#2A2A2A] rounded-full h-8 w-8 justify-center">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 rounded-full"
                    onClick={() => setIsOpen(false)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  {/* <MessageCircle className="h-5 w-5" /> */}
                  <h2 className="font-semibold">Chat Support</h2>
                </div>
                <div>

                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 rounded-full"
                    onClick={clearMessages}
                    title="Clear chat history">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>

              </div>
            </div>

            <ScrollArea
              className="flex-1 p-4"
              ref={scrollRef}
              onWheel={(e) => {
                const element = e.currentTarget
                const isAtBottom =
                  element.scrollHeight - element.scrollTop <=
                  element.clientHeight + 100
                setAutoScroll(isAtBottom)
              }}>
              {messages.length === 0 && showWelcome && (
                <div className="text-center text-white p-4 animate-in fade-in-50 duration-500">
                  <MessageCircle className="h-8 w-8 mx-auto mb-2 text-white" />
                  <p className="text-sm font-medium mb-2">
                    Welcome to Secrets Hideaway Support!
                  </p>
                  <p className="text-xs">
                    I can help you with:
                    <br />• Wallet functionality
                    <br />• Token swapping
                    <br />• Menu ordering
                    <br />• Membership and waivers
                  </p>
                </div>
              )}

              <div className="space-y-6">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      'flex flex-col space-y-2',
                      'animate-in slide-in-from-bottom-1 duration-200',
                      message.role === 'user' ? 'items-end' : 'items-start'
                    )}>
                    <div
                      className={cn(
                        'rounded-lg p-4 justify-center items-center flex max-w-[80%] shadow-sm transition-all',
                        message.role === 'user'
                          ? 'bg-white text-[#18181B]'
                          : 'bg-[#303030] hover:bg-[#303030]/80 text-white'
                      )}>
                      <div
                        className={cn(
                          'prose prose-sm max-w-none',
                          message.role === 'user'
                            ? 'prose-p:text-[#18181B] prose-li:text-[#18181B] prose-a:text-[#18181B]'
                            : 'prose-p:text-white prose-li:text-white prose-a:text-white'
                        )}
                      >
                        {renderMarkdown(message.content)}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground px-2">
                      {message.timestamp instanceof Date
                        ? message.timestamp.toLocaleTimeString()
                        : new Date(message.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg w-max animate-in fade-in-0 duration-200">
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce [animation-delay:-0.3s]" />
                        <div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce [animation-delay:-0.15s]" />
                        <div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" />
                      </div>
                      <span className="text-sm text-white">
                        AI is typing...
                      </span>
                    </div>
                  </div>
                )}

                {error && (
                  <Alert
                    variant="destructive"
                    className="animate-in fade-in-0 slide-in-from-top-2 duration-200">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="flex items-center justify-between">
                      <span>{error}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (retryMessage) {
                            sendMessage(retryMessage)
                          }
                        }}
                        disabled={!retryMessage || isTyping}
                        className="ml-2">
                        Retry
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                {!autoScroll && messages.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="fixed bottom-[80px] right-4 animate-in fade-in-0"
                    onClick={() => {
                      if (scrollRef.current) {
                        scrollRef.current.scrollTo({
                          top: scrollRef.current.scrollHeight,
                          behavior: 'smooth'
                        })
                        setAutoScroll(true)
                      }
                    }}>
                    Scroll to Bottom
                  </Button>
                )}
              </div>
            </ScrollArea>

            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="p-4 bg-[#18181B] backdrop-blur supports-[backdrop-filter]:bg-[#18181B]/80">
              <div className="flex space-x-2 relative">
                <Input
                  {...form.register('message')}
                  placeholder="Type your message..."
                  className="flex-1 rounded-full pl-6"
                  disabled={isTyping}
                />
                <Button
                  type="submit"
                  disabled={isTyping}
                  className={cn(
                    'bg-transparent absolute right-4 top-1/2 -translate-y-1/2 p-0',
                    'transition-all duration-200',
                    isTyping && 'opacity-50'
                  )}>
                  {isTyping ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <SendHorizontal color="#DC2626" className="p-0"/>
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </ErrorBoundary>
  )
}
